// src/admin/screens/loans/tabs/NotesAuditTab.tsx
// Side-by-side: admin notes pinned to this loan + audit log entries whose
// target is this loan. Schema alignment: our audit table columns are
// target_table / diff_json (not target_type / payload).
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import Card from '@admin/components/Card';
import SectionHead from '@admin/components/SectionHead';
import { formatCatDateTime } from '@admin/utils/dates';
import type { Database, Json } from '@/lib/database.types';
import type { LoanDetail } from '@admin/hooks/useLoan';
import styles from './NotesAuditTab.module.css';

type NoteRow = Database['public']['Tables']['borrower_notes']['Row'];
type AuditRow = Database['public']['Tables']['admin_audit_log']['Row'];

interface Props {
  loan: LoanDetail;
}

export default function NotesAuditTab({ loan }: Props) {
  const { data: notes } = useQuery<NoteRow[]>({
    queryKey: ['borrower-notes-for-loan', loan.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('borrower_notes')
        .select('*')
        .eq('loan_id', loan.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as NoteRow[];
    },
  });

  const { data: audit } = useQuery<AuditRow[]>({
    queryKey: ['audit-for-loan', loan.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select('*')
        .eq('target_table', 'loan')
        .eq('target_id', loan.id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
  });

  const noteCount = notes?.length ?? 0;
  const auditCount = audit?.length ?? 0;

  return (
    <div className={styles.root}>
      <SectionHead
        eyebrow="History"
        title="Notes & audit"
        description={`${noteCount} note${noteCount === 1 ? '' : 's'} · ${auditCount} audit entr${auditCount === 1 ? 'y' : 'ies'} on this loan`}
      />
      <div className={styles.grid}>
        <Card title="Notes">
          {(notes ?? []).length === 0 ? (
            <div className={styles.empty}>No notes on this loan yet.</div>
          ) : (
            (notes ?? []).map((n) => (
              <div key={n.id} className={styles.row}>
                <div className={styles.rowHead}>
                  <span className={styles.timestamp}>{formatCatDateTime(n.created_at)}</span>
                </div>
                <p className={styles.body}>{n.body}</p>
              </div>
            ))
          )}
        </Card>
        <Card title="Audit log">
          {(audit ?? []).length === 0 ? (
            <div className={styles.empty}>No audit entries yet.</div>
          ) : (
            (audit ?? []).map((a) => (
              <div key={a.id} className={styles.row}>
                <div className={styles.rowHead}>
                  <span className={styles.actionTag}>{a.action}</span>
                  <span className={styles.timestamp}>{formatCatDateTime(a.created_at)}</span>
                </div>
                {summariseDiff(a.diff_json) && (
                  <div className={styles.diff}>{summariseDiff(a.diff_json)}</div>
                )}
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

function summariseDiff(diff: Json | null): string {
  if (!diff || typeof diff !== 'object' || Array.isArray(diff)) return '';
  const keys = Object.keys(diff as Record<string, unknown>);
  if (keys.length === 0) return '';
  if (keys.length <= 3) return keys.join(', ');
  return `${keys.slice(0, 3).join(', ')} +${keys.length - 3}`;
}
